import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';

// Providers
import { AuthProvider } from '@/features/auth/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';

// Components
import Header from '@/components/Header';
import Sidebar from '@/components/Sidebar';
import ErrorFallback from '@/components/ErrorBoundary';
import ToastProvider from '@/components/ToastProvider';
import SimpleTour from '@/components/SimpleTour';
import Onboarding from '@/components/Onboarding';
import OnboardingGuard from '@/components/OnboardingGuard';
import { OnboardingStorage } from '@/utils/onboarding.utils';

// Pages
import ApplicationHub from '@/pages/ApplicationHub';
import Applications from '@/pages/Applications';
import EssayCoach from '@/pages/EssayCoach';
import CollegeListBuilder from '@/pages/CollegeListBuilder';
import Deadlines from '@/pages/Deadlines';
import Progress from '@/pages/Progress';
import Resources from '@/pages/Resources';
import Messages from '@/pages/Messages';
import Settings from '@/pages/Settings';
import TextAnalyzer from '@/components/TextAnalyzer';
import AnalysisResultsPage from '@/pages/AnalysisResultsPage';
import PaymentPage from '@/components/PaymentPage';
import LoginPage from '@/features/auth/pages/LoginPage';
import SignupPage from '@/features/auth/pages/SignupPage';
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage';
import AdminPanel from '@/pages/AdminPanel';

// Hooks
import { useAuth } from '@/features/auth/context/AuthContext';


// Types
import { User } from '@/types';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000, // Changed from cacheTime to gcTime
      retry: 3,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

interface PrivateRouteProps {
  user: User | null;
  loading: boolean;
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ user, loading, children }) => {
  const location = useLocation();
  
  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return (
    <OnboardingGuard user={user}>
      {children}
    </OnboardingGuard>
  );
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, shouldShowTour, dismissTour, startTour } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  const routesWithHeader = ['/analyze/', '/result/', '/college-list-builder'];
  const shouldShowHeader = user && routesWithHeader.some(route => 
    location.pathname.startsWith(route)
  );

  useEffect(() => {
    if (user && !loading) {
      const shouldShowOnboardingFlow = OnboardingStorage.needsOnboarding(user);
      setShowOnboarding(shouldShowOnboardingFlow);
    }
  }, [user, loading]);

  useEffect(() => {
    if (user) {
      const needsOnboarding = OnboardingStorage.needsOnboarding(user);
      
      if (!needsOnboarding) {
        const shouldNavigate = localStorage.getItem('navigate_to_college_list');
        if (shouldNavigate === 'true' && window.location.pathname !== '/college-list-builder') {
          localStorage.removeItem('navigate_to_college_list');
          navigate('/college-list-builder');
        }
      }
    }
  }, [navigate, user]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setTimeout(() => {
      startTour();
      if (window.location.pathname !== '/college-list-builder') {
        navigate('/college-list-builder');
      }
    }, 500);
  };

  if(loading) 
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    )

  if (user && showOnboarding) {
    return <Onboarding user={user} onComplete={handleOnboardingComplete} />;
  }
    

  return (
    <div className="h-screen flex" style={{
      background: 'var(--bg-secondary)'
    }}>
      {user && (
        <Sidebar 
          user={user}
        />
      )}
      
      <div 
        className="flex flex-col flex-1 app-main-content" 
        style={{
          marginLeft: user ? 'var(--sidebar-width)' : '0',
          transition: 'margin-left 0.3s ease'
        }}
      >
        {shouldShowHeader && (
          <Header
            user={user}
          />
        )}
        
        <main className="flex-1 p-4 overflow-auto">
     
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <LoginPage />}
          />

          <Route
            path="/signup"
            element={user ? <Navigate to="/" replace /> : <SignupPage />}
          />

          <Route
            path="/forgot-password"
            element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
          />
          
          <Route 
            path="/" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <ApplicationHub user={user!} />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/applications" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <Applications user={user} />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/essay-coach" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <EssayCoach />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/college-list-builder" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <CollegeListBuilder />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/deadlines" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <Deadlines user={user} />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/progress" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <Progress user={user} />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/resources" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <Resources user={user} />
              </PrivateRoute>
            } 
          />
          
          <Route
            path="/messages"
            element={
              <PrivateRoute user={user} loading={loading}>
                <Messages user={user} />
              </PrivateRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <PrivateRoute user={user} loading={loading}>
                <Settings user={user!} />
              </PrivateRoute>
            }
          />

          <Route
            path="/analyze/:university"
            element={
              <PrivateRoute user={user} loading={loading}>
                <TextAnalyzer
                  
                />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/result/:university"
            element={
              <PrivateRoute user={user} loading={loading}>
                <AnalysisResultsPage />
              </PrivateRoute>
            }
          />
          
          <Route 
            path="/buy-tokens" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <PaymentPage />
              </PrivateRoute>
            } 
          />
          
          <Route 
            path="/admin" 
            element={
              <PrivateRoute user={user} loading={loading}>
                <AdminPanel />
              </PrivateRoute>
            } 
          />
        </Routes>
        </main>
        
      </div>
      
      {user && (
        <>
          <div className="fixed right-0 top-1/2 transform -translate-y-1/2 flex flex-col space-y-2 z-[9999]">
            <button
              className="report-issue-button bg-pink-300 hover:bg-pink-400 text-gray-800 px-1 py-6 rounded-l-lg transition-colors shadow-lg text-sm"
              onClick={() => window.open('https://airtable.com/appzqLGUk9kKjfji7/pagXjelFBnyc6AJGB/form', '_blank')}
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              Leave us your feedback 
            </button>

            <button
              className="contact-live-chat-button bg-blue-300 hover:bg-blue-400 text-gray-800 px-1 py-6 rounded-l-lg transition-colors shadow-lg text-sm"
              onClick={() => window.open('https://airtable.com/appzqLGUk9kKjfji7/pagvNFvimkwJYkQ0w/form', '_blank')}
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              Get Live Consulting
            </button>
          </div>
          <SimpleTour isOpen={shouldShowTour} onClose={dismissTour} />
        </>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <Router>
              <AppContent />
            </Router>
            <ToastProvider />
            {import.meta.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App; 